import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Tabs, Tab, Paper, Collapse } from '@material-ui/core'
import {
    TableChart as RentRollIcon,
    Description as DocumentsIcon,
    Assignment as AuditIcon,
    BarChart as AnalysisIcon,
    ViewList as AttributesIcon,
    Assessment as ReportsIcon,
    PermContactCalendar as ResidentTermsIcon,
} from '@material-ui/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { axiosInstance } from '../../../helpers'
import { useLeaseAuditDetail } from '../hooks'
import { StepIndicator } from './StepIndicator'
import { DetailHeader } from './DetailHeader'
import {
    STEP_TO_TAB,
    LeaseAuditStep,
    LEASE_AUDIT_STEPS,
    isStepReached,
} from '../types'
import { RentRollRoot } from './RentRollTab/RentRollRoot'
import { LeaseDocumentsRoot } from './LeaseDocumentsTab/LeaseDocumentsRoot'
import { AuditRoot } from './AuditTab/AuditRoot'
import { ReportsRoot } from './ReportsTab/ReportsRoot'
import { AnalysisRoot } from './AnalysisTab/AnalysisRoot'
import { AttributesRoot } from './AttributesTab/AttributesRoot'
import { useLeaseAuditDetailStyles } from './styles'
import { ResidentTermsRoot } from './ResidentTermsTab/ResidentTermsRoot'
import { TermSelection } from './TermSelection'

interface TabPanelProps {
    children: React.ReactNode
    value: number
    index: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            style={{
                display: value === index ? 'flex' : 'none',
                flexDirection: 'column',
                flex: 1,
                overflow: 'hidden',
            }}
        >
            {value === index && children}
        </div>
    )
}

/**
 * Determines if a tab should be enabled based on the latest ready step
 * @param tabIndex - The index of the tab (0-based)
 * @param latestReadyStep - The latest step that has been reached in the workflow
 * @returns true if the tab should be enabled
 */
const isTabEnabled = (
    tabIndex: number,
    latestReadyStep: LeaseAuditStep,
): boolean => {
    switch (tabIndex) {
        case 0: // Rent Roll
            return true
        case 1: // Lease Documents
            return isStepReached(latestReadyStep, 'DOCUMENT_UPLOAD')
        case 2: // Resident Terms
            return isStepReached(latestReadyStep, 'DOCUMENT_UPLOAD')
        case 3: // Audit
            return isStepReached(latestReadyStep, 'AUDITING')
        case 4: // Analysis
            return isStepReached(latestReadyStep, 'AUDITING')
        case 5: // Reports
            return isStepReached(latestReadyStep, 'AUDITING')
        case 6: // Attributes
            return isStepReached(latestReadyStep, 'ATTRIBUTE_SELECTION')
        default:
            return false
    }
}

/**
 * Finds the first enabled tab index
 * @param latestReadyStep - The latest step that has been reached in the workflow
 * @returns The index of the first enabled tab
 */
const findFirstEnabledTab = (latestReadyStep: LeaseAuditStep): number => {
    // Check tabs in order: 0, 1, 2, 3, 4, 5, 6
    for (let i = 0; i < 7; i++) {
        if (isTabEnabled(i, latestReadyStep)) {
            return i
        }
    }
    // Fallback to tab 0 (Rent Roll is always enabled)
    return 0
}

export const LeaseAuditDetail: React.FC = () => {
    const classes = useLeaseAuditDetailStyles()
    const { id } = useParams<{ id: string }>()
    const auditId = Number(id)

    const [activeTab, setActiveTab] = useState(0)
    const [showStepIndicator, setShowStepIndicator] = useState(true)
    const [hasAutoHidden, setHasAutoHidden] = useState(false)
    const [showTermSelection, setShowTermSelection] = useState(false)
    const [showUploadZone, setShowUploadZone] = useState(false)
    const {
        leaseAudit,
        isLoading,
        isGeneratingReport,
        generateReport,
        updateLeaseAudit,
    } = useLeaseAuditDetail({ auditId })

    // Check if we're currently processing (using task IDs from model)
    const isProcessingRentRoll = !!leaseAudit?.rent_roll_processing_task_id
    const isProcessingParsing = !!leaseAudit?.parsing_task_id
    const extractionTaskId =
        leaseAudit?.data_extraction_task_id ?? leaseAudit?.extraction_task_id
    const isProcessingExtraction = !!extractionTaskId

    const queryClient = useQueryClient()

    // Mutation to begin document parsing
    const beginParseMutation = useMutation({
        mutationFn: () =>
            axiosInstance.post(`/lease-audit/${auditId}/documents/parse/`),
        onSuccess: () => {
            // Invalidate lease audit detail to get updated parsing_task_id
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
        },
    })

    // Mutation to begin data extraction
    const beginExtractionMutation = useMutation({
        mutationFn: () =>
            axiosInstance.post(
                `/lease-audit/${auditId}/documents/extract-data/`,
            ),
        onSuccess: () => {
            // Invalidate lease audit detail to get updated extraction task id
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
        },
    })

    // Auto-select tab based on current_step when data loads initially
    // but don't override user tab changes after that
    const [hasInitialized, setHasInitialized] = React.useState(false)
    useEffect(() => {
        if (leaseAudit?.current_step && !hasInitialized) {
            const tabIndex = STEP_TO_TAB[leaseAudit.current_step]
            const latestReadyStep = leaseAudit.latest_ready_step ?? 'CREATE'

            // Validate that the tab is enabled, otherwise find the first enabled tab
            if (isTabEnabled(tabIndex, latestReadyStep)) {
                setActiveTab(tabIndex)
            } else {
                setActiveTab(findFirstEnabledTab(latestReadyStep))
            }
            setHasInitialized(true)
        }
    }, [
        leaseAudit?.current_step,
        leaseAudit?.latest_ready_step,
        hasInitialized,
    ])

    // Validate active tab when latest_ready_step changes
    useEffect(() => {
        if (leaseAudit?.latest_ready_step && hasInitialized) {
            const latestReadyStep = leaseAudit.latest_ready_step

            // If current active tab is now disabled, switch to first enabled tab
            if (!isTabEnabled(activeTab, latestReadyStep)) {
                setActiveTab(findFirstEnabledTab(latestReadyStep))
            }
        }
    }, [leaseAudit?.latest_ready_step, activeTab, hasInitialized])

    // Auto-hide step indicator when reaching AUDITING step (only once)
    useEffect(() => {
        if (leaseAudit?.current_step === 'AUDITING' && !hasAutoHidden) {
            setShowStepIndicator(false)
            setHasAutoHidden(true)
        }
    }, [leaseAudit?.current_step, hasAutoHidden])

    const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
        setActiveTab(newValue)
    }

    const handleStepClick = (step: LeaseAuditStep) => {
        // Update current_step in backend and navigate to the corresponding tab
        const tabIndex = STEP_TO_TAB[step]
        setActiveTab(tabIndex)

        // Only update current_step if it's different
        if (leaseAudit?.current_step !== step) {
            updateLeaseAudit({ current_step: step })

            // If transitioning to DOCUMENT_PARSING, trigger the parse process
            if (step === 'DOCUMENT_PARSING' && !leaseAudit?.parsing_task_id) {
                beginParseMutation.mutate()
            }

            // If transitioning TO DOCUMENT_REVIEW, invalidate resident terms to fetch fresh data
            if (step === 'DOCUMENT_REVIEW') {
                queryClient.invalidateQueries({
                    queryKey: ['residentTerms', auditId],
                })
            }

            // If transitioning to DATA_EXTRACTION, trigger the extraction process
            if (step === 'DATA_EXTRACTION' && !extractionTaskId) {
                beginExtractionMutation.mutate()
            }
        }
    }

    const handleNextStep = () => {
        // Get the next step
        const currentStepIndex = LEASE_AUDIT_STEPS.indexOf(
            leaseAudit?.current_step ?? 'RENT_ROLL_PROCESSING',
        )
        const nextStepIndex = currentStepIndex + 1
        const nextStep = LEASE_AUDIT_STEPS[nextStepIndex]

        if (nextStep) {
            handleStepClick(nextStep)
        }
    }

    const handleGenerateReport = () => {
        generateReport()
    }

    const handleToggleStepIndicator = () => {
        setShowStepIndicator((prev) => !prev)
        if (showTermSelection) {
            setShowTermSelection(false)
        }
    }

    const handleToggleTermSelection = () => {
        setShowTermSelection((prev) => !prev)
        if (showStepIndicator) {
            setShowStepIndicator(false)
        }
    }

    const isCollapsableSelectorOpen = showTermSelection || showStepIndicator

    return (
        <Box className={classes.root}>
            {/* Header with back button, title, and action buttons */}
            <DetailHeader
                leaseAudit={leaseAudit}
                isLoading={isLoading}
                isGeneratingReport={isGeneratingReport}
                onGenerateReport={handleGenerateReport}
                showStepIndicator={showStepIndicator}
                onToggleStepIndicator={handleToggleStepIndicator}
                showTermSelection={showTermSelection}
                onToggleTermSelection={handleToggleTermSelection}
            />

            {/* Step Indicator */}
            <Collapse in={showStepIndicator}>
                <Paper className={classes.stepperContainer} elevation={0}>
                    <StepIndicator
                        currentStep={
                            leaseAudit?.current_step ?? 'RENT_ROLL_PROCESSING'
                        }
                        latestReadyStep={
                            leaseAudit?.latest_ready_step ??
                            'RENT_ROLL_PROCESSING'
                        }
                        isProcessing={
                            isProcessingRentRoll ||
                            isProcessingParsing ||
                            isProcessingExtraction
                        }
                        onStepClick={handleStepClick}
                        onNextStep={handleNextStep}
                    />
                </Paper>
            </Collapse>

            <Collapse in={showTermSelection}>
                <Paper className={classes.termSelectionContainer} elevation={0}>
                    <TermSelection auditId={auditId} />
                </Paper>
            </Collapse>

            {/* Tab Navigation */}
            <Paper className={classes.tabsContainer} elevation={0}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    className={classes.tabs}
                >
                    {/* Rent Roll Tab */}
                    <Tab
                        label={
                            <Box display="flex" alignItems="center">
                                <RentRollIcon className={classes.tabIcon} />
                                Rent Roll
                            </Box>
                        }
                        className={classes.tab}
                        disabled={
                            !isTabEnabled(
                                0,
                                leaseAudit?.latest_ready_step ?? 'CREATE',
                            )
                        }
                    />
                    {/* Lease Documents Tab */}
                    <Tab
                        label={
                            <Box display="flex" alignItems="center">
                                <DocumentsIcon className={classes.tabIcon} />
                                Lease Documents
                            </Box>
                        }
                        className={classes.tab}
                        disabled={
                            !isTabEnabled(
                                1,
                                leaseAudit?.latest_ready_step ?? 'CREATE',
                            )
                        }
                    />
                    {/* Resident Terms Tab */}
                    <Tab
                        label={
                            <Box display="flex" alignItems="center">
                                <ResidentTermsIcon
                                    className={classes.tabIcon}
                                />
                                Resident Terms
                            </Box>
                        }
                        className={classes.tab}
                        disabled={
                            !isTabEnabled(
                                2,
                                leaseAudit?.latest_ready_step ?? 'CREATE',
                            )
                        }
                    />
                    {/* Audit Tab */}
                    <Tab
                        label={
                            <Box display="flex" alignItems="center">
                                <AuditIcon className={classes.tabIcon} />
                                Audit
                            </Box>
                        }
                        className={classes.tab}
                        disabled={
                            !isTabEnabled(
                                3,
                                leaseAudit?.latest_ready_step ?? 'AUDITING',
                            )
                        }
                    />
                    {/* Analysis Tab */}
                    <Tab
                        label={
                            <Box display="flex" alignItems="center">
                                <AnalysisIcon className={classes.tabIcon} />
                                Analysis
                            </Box>
                        }
                        className={classes.tab}
                        disabled={
                            !isTabEnabled(
                                4,
                                leaseAudit?.latest_ready_step ?? 'AUDITING',
                            )
                        }
                    />
                    {/* Reports Tab */}
                    <Tab
                        label={
                            <Box display="flex" alignItems="center">
                                <ReportsIcon className={classes.tabIcon} />
                                Reports
                            </Box>
                        }
                        className={classes.tab}
                        disabled={
                            !isTabEnabled(
                                5,
                                leaseAudit?.latest_ready_step ?? 'CREATE',
                            )
                        }
                    />
                    {/* Attributes Tab */}
                    <Tab
                        label={
                            <Box display="flex" alignItems="center">
                                <AttributesIcon className={classes.tabIcon} />
                                Attributes
                            </Box>
                        }
                        className={classes.tab}
                        disabled={
                            !isTabEnabled(
                                6,
                                leaseAudit?.latest_ready_step ?? 'CREATE',
                            )
                        }
                    />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            <Box className={classes.contentContainer}>
                <TabPanel value={activeTab} index={0}>
                    <RentRollRoot
                        auditId={auditId}
                        rentRollProcessingTaskId={
                            leaseAudit?.rent_roll_processing_task_id
                        }
                        isCollapsableSelectorOpen={isCollapsableSelectorOpen}
                    />
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <LeaseDocumentsRoot
                        auditId={auditId}
                        currentStep={
                            leaseAudit?.current_step ?? 'DOCUMENT_UPLOAD'
                        }
                        parsingTaskId={leaseAudit?.parsing_task_id}
                        extractionTaskId={extractionTaskId}
                        isCollapsableSelectorOpen={isCollapsableSelectorOpen}
                        showUploadZone={showUploadZone}
                        onOpenUploadZone={() => setShowUploadZone(true)}
                        onCloseUploadZone={() => setShowUploadZone(false)}
                    />
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                    <ResidentTermsRoot
                        auditId={auditId}
                        isCollapsableSelectorOpen={isCollapsableSelectorOpen}
                        onOpenUpload={() => {
                            setShowUploadZone(true)
                            setActiveTab(1)
                        }}
                    />
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                    <AuditRoot
                        auditId={auditId}
                        onGenerateReport={handleGenerateReport}
                        isGeneratingReport={isGeneratingReport}
                        isCollapsableSelectorOpen={isCollapsableSelectorOpen}
                    />
                </TabPanel>

                <TabPanel value={activeTab} index={4}>
                    <AnalysisRoot auditId={auditId} />
                </TabPanel>

                <TabPanel value={activeTab} index={5}>
                    <ReportsRoot auditId={auditId} />
                </TabPanel>

                <TabPanel value={activeTab} index={6}>
                    <AttributesRoot auditId={auditId} />
                </TabPanel>
            </Box>
        </Box>
    )
}
